import { useState, useEffect, useRef, useCallback } from 'react'
import FullCalendar from '@fullcalendar/react'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import interactionPlugin from '@fullcalendar/interaction'
import type { EventInput, DateSelectArg, EventClickArg } from '@fullcalendar/core'
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import EventoModal from '../../../components/calendario/EventoModal'
import EventoDetailModal from '../../../components/calendario/EventoDetailModal'
import type { RisorsaCalendario, EventoCalendario, TipoEvento, TIPO_EVENTO_CONFIG as TipoConfig } from '../../../types/calendario'
import { TIPO_EVENTO_CONFIG } from '../../../types/calendario'

type ViewType = 'resourceTimelineWeek' | 'resourceTimelineMonth'

export default function CalendarioPage() {
  const calRef = useRef<FullCalendar>(null)
  const [risorse, setRisorse] = useState<RisorsaCalendario[]>([])
  const [eventi, setEventi] = useState<EventoCalendario[]>([])
  const [currentTitle, setCurrentTitle] = useState('')
  const [currentView, setCurrentView] = useState<ViewType>('resourceTimelineWeek')
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning'; duration?: number } | null>(null)

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createDate, setCreateDate] = useState<string | null>(null)
  const [createResourceId, setCreateResourceId] = useState<string | null>(null)
  const [detailGruppoId, setDetailGruppoId] = useState<string | null>(null)

  // Load risorse once
  useEffect(() => {
    const loadRisorse = async () => {
      try {
        const res = await api.get('/v1/calendario/risorse')
        setRisorse(res.data)
      } catch {
        setToast({ message: 'Errore caricamento risorse', type: 'error' })
      }
    }
    loadRisorse()
  }, [])

  // Load eventi when calendar dates change
  const loadEventi = useCallback(async (startStr: string, endStr: string) => {
    setLoading(true)
    try {
      const res = await api.get('/v1/calendario/eventi', {
        params: { data_da: startStr, data_a: endStr }
      })
      setEventi(res.data)
    } catch {
      setToast({ message: 'Errore caricamento eventi', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh after create/update/delete
  const refreshEventi = useCallback(() => {
    const calApi = calRef.current?.getApi()
    if (calApi) {
      const start = calApi.view.activeStart
      const end = calApi.view.activeEnd
      loadEventi(
        start.toISOString().split('T')[0],
        end.toISOString().split('T')[0]
      )
    }
  }, [loadEventi])

  // Convert risorse for FullCalendar
  const fcResources = risorse.map(r => ({
    id: r.id,
    title: r.label,
    gruppo: r.gruppo,
    extendedProps: { tipo: r.tipo, ruolo: r.ruolo, tipologia: r.tipologia }
  }))

  // Convert eventi for FullCalendar
  const fcEvents: EventInput[] = eventi.map(ev => {
    const cfg = TIPO_EVENTO_CONFIG[ev.tipo_evento as TipoEvento] || TIPO_EVENTO_CONFIG.lavoro
    return {
      id: ev.id,
      title: ev.titolo,
      start: ev.data,
      end: ev.data,
      allDay: true,
      resourceId: `${ev.risorsa_tipo}_${ev.risorsa_id}`,
      backgroundColor: cfg.color,
      borderColor: cfg.color,
      textColor: '#ffffff',
      extendedProps: { ...ev }
    }
  })

  // Handlers
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const dateStr = selectInfo.startStr
    const resourceId = selectInfo.resource?.id || null
    setCreateDate(dateStr)
    setCreateResourceId(resourceId)
    setShowCreateModal(true)
  }

  const handleEventClick = (clickInfo: EventClickArg) => {
    const gruppoId = clickInfo.event.extendedProps.gruppo_id
    if (gruppoId) {
      setDetailGruppoId(gruppoId)
    }
  }

  const handleDatesSet = (dateInfo: { start: Date; end: Date; view: { title: string } }) => {
    setCurrentTitle(dateInfo.view.title)
    loadEventi(
      dateInfo.start.toISOString().split('T')[0],
      dateInfo.end.toISOString().split('T')[0]
    )
  }

  const handlePrev = () => calRef.current?.getApi().prev()
  const handleNext = () => calRef.current?.getApi().next()
  const handleToday = () => calRef.current?.getApi().today()

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view)
    calRef.current?.getApi().changeView(view)
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="w-7 h-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Calendario Risorse</h1>
        </div>
        <button
          onClick={() => { setCreateDate(new Date().toISOString().split('T')[0]); setCreateResourceId(null); setShowCreateModal(true) }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nuovo Evento
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 bg-white rounded-lg border border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={handleToday}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Oggi
          </button>
          <button
            onClick={handleNext}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
          <span className="ml-3 text-lg font-semibold text-gray-900 capitalize">
            {currentTitle}
          </span>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => handleViewChange('resourceTimelineWeek')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              currentView === 'resourceTimelineWeek'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Settimana
          </button>
          <button
            onClick={() => handleViewChange('resourceTimelineMonth')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              currentView === 'resourceTimelineMonth'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mese
          </button>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-4 mb-4 px-1">
        {(Object.entries(TIPO_EVENTO_CONFIG) as [TipoEvento, typeof TipoConfig[TipoEvento]][]).map(([tipo, cfg]) => (
          <div key={tipo} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: cfg.color }} />
            <span className="text-xs text-gray-600">{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${loading ? 'opacity-60' : ''}`}>
        <FullCalendar
          ref={calRef}
          plugins={[resourceTimelinePlugin, interactionPlugin]}
          schedulerLicenseKey="0935715720-fcs-1769956551"
          initialView="resourceTimelineWeek"
          locale="it"
          headerToolbar={false}
          height="auto"
          resources={fcResources}
          events={fcEvents}
          resourceGroupField="gruppo"
          resourceAreaWidth="220px"
          resourceAreaHeaderContent="Risorse"
          slotMinWidth={80}
          selectable={true}
          select={handleDateSelect}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          resourceLabelContent={(arg) => {
            const tipo = arg.resource.extendedProps?.tipo
            const icon = tipo === 'personale' ? '\u{1F464}' : '\u{1F527}'
            return { html: `<span class="text-sm">${icon} ${arg.resource.title}</span>` }
          }}
          eventContent={(arg) => {
            const ev = arg.event.extendedProps as EventoCalendario
            const ore = ev.ore ?? 8
            return {
              html: `<div class="px-1.5 py-0.5 text-xs leading-tight truncate">
                <strong>${arg.event.title}</strong>
                ${ore !== 8 ? `<span class="opacity-75"> (${ore}h)</span>` : ''}
              </div>`
            }
          }}
          views={{
            resourceTimelineWeek: {
              type: 'resourceTimeline',
              duration: { weeks: 1 },
              slotDuration: { days: 1 },
              slotLabelFormat: { weekday: 'short', day: 'numeric', month: 'short' },
            },
            resourceTimelineMonth: {
              type: 'resourceTimeline',
              duration: { months: 1 },
              slotDuration: { days: 1 },
              slotLabelFormat: { weekday: 'narrow', day: 'numeric' },
            },
          }}
        />
      </div>

      {/* Modals */}
      {showCreateModal && (
        <EventoModal
          date={createDate!}
          preselectedResourceId={createResourceId}
          risorse={risorse}
          onClose={() => { setShowCreateModal(false); setCreateDate(null); setCreateResourceId(null) }}
          onCreated={(data?: any) => {
            setShowCreateModal(false)
            setCreateDate(null)
            setCreateResourceId(null)
            refreshEventi()
            if (data?.commessa_auto_avviata) {
              setToast({ message: `Evento creato — Commessa avviata automaticamente (inizio: ${data.commessa_data_inizio})`, type: 'success', duration: 6000 })
            } else {
              setToast({ message: 'Evento creato', type: 'success' })
            }
          }}
        />
      )}

      {detailGruppoId && (
        <EventoDetailModal
          gruppoId={detailGruppoId}
          onClose={() => setDetailGruppoId(null)}
          onUpdated={() => {
            refreshEventi()
            setToast({ message: 'Evento aggiornato', type: 'success' })
          }}
          onDeleted={() => {
            setDetailGruppoId(null)
            refreshEventi()
            setToast({ message: 'Evento eliminato', type: 'success' })
          }}
        />
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={() => setToast(null)} />
      )}
    </div>
  )
}
